import React, { useState } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Card, CardContent } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { generateClient } from 'aws-amplify/api';
import { getUserWatchedQuery, deleteWatchedItemMutation } from '../../graphql/watchlist';

const WatchedCleanup = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const cleanupDuplicates = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setResults(null);
    
    try {
      const client = generateClient();
      
      // Get all watched items for user
      const result = await client.graphql({
        query: getUserWatchedQuery,
        variables: { userID: user.id, limit: 1000 }
      });
      
      const watchedItems = result.data?.listWatchedItems?.items || [];
      console.log('Total watched items:', watchedItems.length);
      
      // Group by movieID to find duplicates
      const movieGroups = {};
      watchedItems.forEach(item => {
        if (!movieGroups[item.movieID]) {
          movieGroups[item.movieID] = [];
        }
        movieGroups[item.movieID].push(item);
      });
      
      // Find duplicates
      const duplicates = [];
      const toDelete = [];
      
      Object.entries(movieGroups).forEach(([movieID, items]) => {
        if (items.length > 1) {
          duplicates.push({ movieID, count: items.length });
          // Keep the oldest item, delete the rest
          const sorted = items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          toDelete.push(...sorted.slice(1));
        }
      });
      
      console.log('Duplicates found:', duplicates);
      console.log('Items to delete:', toDelete.length);
      
      // Delete duplicates
      let deletedCount = 0;
      for (const item of toDelete) {
        try {
          await client.graphql({
            query: deleteWatchedItemMutation,
            variables: { input: { id: item.id } }
          });
          deletedCount++;
        } catch (error) {
          console.error('Error deleting item:', item.id, error);
        }
      }
      
      setResults({
        totalItems: watchedItems.length,
        duplicateMovies: duplicates.length,
        deletedItems: deletedCount,
        duplicates: duplicates
      });
      
      addNotification({
        type: 'success',
        message: `Cleanup complete! Removed ${deletedCount} duplicate entries`
      });
      
    } catch (error) {
      console.error('Error during cleanup:', error);
      addNotification({
        type: 'error',
        message: 'Failed to cleanup duplicates'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to cleanup watched list</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Watched List Cleanup
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        This tool will remove duplicate entries from your watched list. 
        It keeps the oldest entry for each movie and removes the rest.
      </Alert>
      
      <Button
        variant="contained"
        onClick={cleanupDuplicates}
        disabled={loading}
        size="large"
        sx={{ mb: 3 }}
      >
        {loading ? 'Cleaning up...' : 'Clean Up Duplicates'}
      </Button>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <CircularProgress />
        </Box>
      )}
      
      {results && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cleanup Results
            </Typography>
            
            <Typography variant="body1" gutterBottom>
              • Total watched items: {results.totalItems}
            </Typography>
            <Typography variant="body1" gutterBottom>
              • Movies with duplicates: {results.duplicateMovies}
            </Typography>
            <Typography variant="body1" gutterBottom>
              • Duplicate entries removed: {results.deletedItems}
            </Typography>
            
            {results.duplicates.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Movies that had duplicates:
                </Typography>
                {results.duplicates.map((dup, index) => (
                  <Typography key={index} variant="body2" color="text.secondary">
                    • Movie ID {dup.movieID}: {dup.count} entries (removed {dup.count - 1})
                  </Typography>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default WatchedCleanup;